'use client';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { getPriceValueTranslation } from '@/entities/cart';
import { cn } from '@/shared/lib/cn';
import { Currency } from '@/shared/model/Currency';
import { PriceValue } from '@/shared/model/PriceValue';
import { Form } from '@/shared/ui/form';
import { ProductDetailAddonsItemDescription } from './product-detail-addons-item-description';
import { ProductDetailAddonsItemName } from './product-detail-addons-item-name';
import './product-detail.scss';

type Props = {
    name: string;
    value: string;
    description?: string;
    priceValue?: PriceValue;
    imageUrl?: string;
    isEdited?: boolean;
    contained?: number;
    amount?: number;
    packagePrice?: PriceValue;
};

export const ProductDetailAddonsRadioItemRow = ({
    name,
    value,
    description,
    priceValue,
    imageUrl,
    isEdited,
    contained = 0,
    amount = 0,
    packagePrice,
}: Props) => {
    const t = useTranslations();
    const itemAmount = Number(priceValue?.amount ?? 0);
    const pkgAmount = Number(packagePrice?.amount ?? 0);
    const minus = contained > 0 && amount < contained;
    const selected = !minus && (contained > 0 ? amount >= contained : amount > 0);
    const inPrice = contained > 0 && amount <= contained;
    const shownAmount = inPrice ? 0 : itemAmount + pkgAmount;
    const shownCurrency = (priceValue?.currency ?? packagePrice?.currency) as Currency;
    return (
        <label
            className={cn(
                'product-detail__addons-radio-item-row',
                isEdited && !minus && 'product-detail__addons-radio-item-row--edited',
                selected && 'product-detail__addons-radio-item-row--selected',
                minus && 'product-detail__addons-radio-item-row--minus',
            )}
            data-selected={selected ? 'true' : 'false'}
        >
            <Form.RadioGroup.Item
                value={value}
                data-testid="product-detail-addon-radio-item"
                data-value={value}
            >
                <Form.RadioGroup.Indicator />
            </Form.RadioGroup.Item>
            <div className="product-detail__addons-radio-item-row-content">
                {imageUrl && (
                    <Image
                        src={imageUrl}
                        alt="Produkt"
                        width={150}
                        height={118}
                        className="product-detail__addons-radio-item-row-image"
                        data-testid="product-detail-addon-image"
                        data-value={imageUrl}
                    />
                )}
                <div className="product-detail__addons-radio-item-row-info">
                    <ProductDetailAddonsItemName data-testid="product-detail-addon-name">
                        {name}
                        {contained > 0 && (
                            <span
                                className="product-detail__contained-badge"
                                data-testid="product-detail-addon-contained"
                            >
                                {t('PRODUCT_DETAIL.CONTAINED')}
                            </span>
                        )}
                        {packagePrice && (
                            <span
                                className="product-detail__package-badge"
                                data-testid="product-detail-addon-package"
                            >
                                {t('PRODUCT_DETAIL.WITH_PACKAGE', {
                                    price: getPriceValueTranslation({
                                        amount: packagePrice.amount,
                                        currency: packagePrice.currency as Currency,
                                        t,
                                    }),
                                })}
                            </span>
                        )}
                    </ProductDetailAddonsItemName>
                    {description && (
                        <ProductDetailAddonsItemDescription data-testid="product-detail-addon-description">
                            {description}
                        </ProductDetailAddonsItemDescription>
                    )}
                </div>
                {(priceValue || packagePrice) && (
                    <span
                        className={cn(
                            'product-detail__addons-radio-item-row-price',
                            (inPrice || shownAmount === 0) &&
                                'product-detail__addons-radio-item-row-price--included',
                        )}
                        data-testid="product-detail-addon-price"
                        data-value={inPrice || shownAmount === 0 ? '0' : String(shownAmount)}
                    >
                        {inPrice || shownAmount === 0
                            ? t('PRODUCT_DETAIL.IN_PRICE')
                            : `+ ${getPriceValueTranslation({
                                  amount: shownAmount,
                                  currency: shownCurrency,
                                  t,
                              })}`}
                    </span>
                )}
            </div>
        </label>
    );
};
